// src/pages/user/Dashboard.jsx
import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../context/userContext';
import axios from 'axios';
import Loader from '../../components/common/Loader';
import DeletePost from '../post/DeletePost';
import { FaEye, FaEdit, FaPen, FaPlus } from 'react-icons/fa';

const Dashboard = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalViews: 0,
    totalLikes: 0
  });
  
  const { id } = useParams();
  const { currentUser } = useContext(UserContext);
  const token = currentUser?.token;

  // Redirect to login page for any user who isn't logged in
  useEffect(() => {
    if (!token) {
      navigate('/login');
    } else if (currentUser.id !== id) {
      // Redirect if trying to access someone else's dashboard
      navigate(`/dashboard/${currentUser.id}`);
    }
  }, [token, navigate, currentUser, id]);

  // Fetch posts and calculate stats
  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/posts/users/${id}`, 
          {
            withCredentials: true, 
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        const postsData = response.data;
        setPosts(postsData);
        
        // Calculate stats
        const totalViews = postsData.reduce((sum, post) => sum + (post.views || 0), 0);
        const totalLikes = postsData.reduce((sum, post) => sum + (post.likes || 0), 0);
        
        setStats({
          totalPosts: postsData.length,
          totalViews,
          totalLikes
        });
        
        setError(null);
      } catch (err) {
        console.error('Error fetching posts:', err);
        setError('Failed to load your posts. Please try again later.');
        setPosts([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (token && id) {
      fetchPosts();
    }
  }, [id, token]);

  if (isLoading) {
    return (
      <div className="min-h-[300px] flex items-center justify-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <section className="py-8">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Manage your posts and track your content performance
            </p>
          </div>
          
          <Link 
            to="/create" 
            className="mt-4 md:mt-0 bg-primary text-white px-4 py-2 rounded-md inline-flex items-center hover:bg-opacity-90 transition-colors"
          >
            <FaPlus className="mr-2" />
            Create Post
          </Link>
        </div>
        
        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Posts</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-1">{stats.totalPosts}</h3>
              </div>
              <div className="bg-primary bg-opacity-10 p-3 rounded-full">
                <FaPen className="text-primary" size={24} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Views</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-1">{stats.totalViews}</h3>
              </div>
              <div className="bg-blue-500 bg-opacity-10 p-3 rounded-full">
                <FaEye className="text-blue-500" size={24} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Likes</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-1">{stats.totalLikes}</h3>
              </div>
              <div className="bg-red bg-opacity-10 p-3 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="text-red" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        {/* Posts list */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Your Posts</h2>
          </div>
          
          {posts.length === 0 ? (
            <div className="p-8 text-center">
              <h3 className="text-lg font-medium text-gray-700 mb-2">You have no posts yet</h3>
              <p className="text-gray-500 mb-4">Create your first post to get started</p>
              <Link 
                to="/create" 
                className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors"
              >
                <FaPlus className="mr-2" />
                Create Post
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {posts.map(post => (
                <div key={post._id} className="p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center">
                  <div className="flex-1 flex items-center">
                    <div className="w-20 h-16 sm:w-24 sm:h-16 flex-shrink-0 overflow-hidden rounded-md bg-gray-100 mr-4">
                      <img 
                        src={`${process.env.REACT_APP_ASSETS_URL}/uploads/${post.thumbnail}`} 
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h5 className="text-base font-medium text-gray-900 truncate mb-1">
                        {post.title}
                      </h5>
                      <div className="flex items-center text-sm text-gray-500">
                        <span className="mr-3">{new Date(post.createdAt).toLocaleDateString()}</span>
                        <span className="mr-3">{post.views || 0} views</span>
                        <span>{post.likes || 0} likes</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4 sm:mt-0">
                    <Link 
                      to={`/posts/${post._id}`} 
                      className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <FaEye className="mr-1" size={12} />
                      View
                    </Link>
                    
                    <Link 
                      to={`/posts/${post._id}/edit`} 
                      className="inline-flex items-center px-3 py-1 bg-primary text-white text-sm rounded-md hover:bg-opacity-90 transition-colors"
                    >
                      <FaEdit className="mr-1" size={12} />
                      Edit
                    </Link>
                    
                    <DeletePost postId={post._id} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Dashboard;